import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import AppRoutes from "@/constants/routes/AppRoutes";
import {
  clearDocumentViewerContext,
  setDocumentViewerContext,
} from "@/lib/navigation/documentViewerContext";
import {
  buildCanonicalViewerReturnUrl,
  consumePendingNavigationStackMode,
  getChatRootLabel,
  isWorkspaceSearchRoot,
  viewerPathStartsNewTrail,
} from "@/lib/navigation/documentNavigationStackNavigation";
import { getDocumentKeyFromPath } from "@/lib/navigation/documentViewerRoutes";
import {
  clearStack,
  ensureContextFrame,
  getStack,
  pushFrame,
  replaceTopFrame,
  seedStack,
  updateRootFrame,
  updateTopFrame,
} from "@/lib/navigation/documentNavigationStack";
import {
  fromFolderPathSessionKey,
  pathItemsToWorkspaceFilesUrl,
} from "@/lib/navigation/documentViewerReturn";
import type { PathItem } from "@/pages/workspace/utils/folderUtils";

type UseDocumentViewerStackSyncOptions = {
  title: string;
  documentKey?: string | null;
  /** When set, updates the root frame label (e.g. workspace name). */
  rootLabel?: string;
  workspaceId?: string;
};

const seedRootForViewerPath = (pathname: string): void => {
  if (
    pathname.startsWith("/chat/legislation/") ||
    pathname.startsWith("/chat/workspace/")
  ) {
    seedStack([
      {
        label: getChatRootLabel(),
        url: AppRoutes.chat,
        frameKind: "root",
      },
    ]);
    return;
  }

  const workspaceMatch = /^\/workspaces\/([^/]+)/.exec(pathname);
  if (workspaceMatch && pathname.includes("/workspaces/")) {
    const workspaceId = workspaceMatch[1];
    let rootUrl = AppRoutes.workspaceFiles(workspaceId);
    const stored = sessionStorage.getItem(
      fromFolderPathSessionKey(workspaceId)
    );
    if (stored) {
      try {
        const pathItems = JSON.parse(stored) as PathItem[];
        if (Array.isArray(pathItems)) {
          rootUrl = pathItemsToWorkspaceFilesUrl(workspaceId, pathItems);
        }
      } catch {
        // Keep default root URL.
      }
    }
    seedStack([
      {
        label: "Workspace",
        url: rootUrl,
        frameKind: "root",
      },
    ]);
    return;
  }

  if (pathname.includes("/legislation/")) {
    seedStack([
      {
        label: "Kilder",
        url: AppRoutes.legislations,
        frameKind: "root",
      },
    ]);
  }
};

export const useDocumentViewerStackSync = ({
  title,
  documentKey,
  rootLabel,
  workspaceId,
}: UseDocumentViewerStackSyncOptions): void => {
  const location = useLocation();

  useEffect(() => {
    if (viewerPathStartsNewTrail(getStack(), location.pathname)) {
      // Otherwise the block below relabels e.g. the Kilder root as this workspace and
      // the law the reader came from stays in the trail (LAR-658).
      clearStack();
    }
    if (getStack().length === 0) {
      // Seed before the relabelling below, so a fresh trail gets the workspace name too.
      seedRootForViewerPath(location.pathname);
    }
    if (isWorkspaceSearchRoot(getStack()[0])) {
      // Opened from the Workspaces search: the root stays that search, so "Tilbage"
      // returns to it, and the document's workspace becomes a crumb one click away.
      if (workspaceId) {
        ensureContextFrame({
          label: rootLabel ?? "Workspace",
          url: AppRoutes.workspaceFiles(workspaceId),
          frameKind: "context",
        });
      }
    } else {
      if (rootLabel) {
        updateRootFrame({ label: rootLabel });
      }

      if (workspaceId) {
        const stored = sessionStorage.getItem(
          fromFolderPathSessionKey(workspaceId)
        );
        if (stored) {
          try {
            const pathItems = JSON.parse(stored) as PathItem[];
            if (Array.isArray(pathItems)) {
              updateRootFrame({
                url: pathItemsToWorkspaceFilesUrl(workspaceId, pathItems),
              });
            }
          } catch {
            // Ignore invalid session storage.
          }
        }
      }
    }

    const key =
      documentKey ??
      getDocumentKeyFromPath(location.pathname) ??
      location.pathname;
    const returnUrl = buildCanonicalViewerReturnUrl(
      location.pathname,
      location.search
    );
    const effectiveTitle = title.trim() || "Dokument";

    setDocumentViewerContext({
      title: effectiveTitle,
      returnUrl,
      documentKey: key,
    });

    let stack = getStack();
    if (stack.length === 0) {
      seedRootForViewerPath(location.pathname);
      stack = getStack();
    }

    const documentFrame = {
      label: effectiveTitle,
      url: returnUrl,
      documentKey: key,
      frameKind: "document" as const,
    };

    const top = stack[stack.length - 1];
    if (top?.documentKey === key) {
      updateTopFrame({
        label: effectiveTitle,
        url: returnUrl,
      });
    } else {
      const mode = consumePendingNavigationStackMode();
      if (mode === "replace" && stack.length > 0) {
        replaceTopFrame(documentFrame);
      } else {
        pushFrame(documentFrame);
      }
    }

    return () => {
      clearDocumentViewerContext();
    };
  }, [
    title,
    documentKey,
    rootLabel,
    workspaceId,
    location.pathname,
    location.search,
  ]);
};

/** @deprecated Use useDocumentViewerStackSync */
export const useDocumentViewerContextSync = useDocumentViewerStackSync;
